/**
 * TaskCard + TaskStats — from the "Tasks" design, 6a and 6b.
 * @file src/web/features/tasks/components/TaskCard.tsx
 *
 * @llm-rule WHEN: Listing tasks, whether you gave them or were given them
 * @llm-rule NOTE: The border does the talking. Overdue is a heavy accent frame,
 *   live work is a solid frame, not-yet-started and finished are dashed. You
 *   can tell the state of a list at arm's length without reading a pill.
 * @llm-rule AVOID: Defining these inside the page — the delay-reason box on the
 *   same screen is a text input, and a remounting parent steals its focus.
 */

import { Button } from '@bloomneo/uikit';
import { AlarmClock, BadgeCheck, Check, Play } from 'lucide-react';
import { PersonAvatar } from '../../../shared/components/presence';
import { ROLE_LABEL, type OrgRole } from '../../../shared/domain';

export type TaskPriority = 'LOW' | 'NORMAL' | 'URGENT';

export interface TaskLike {
  id: string;
  title: string;
  description: string | null;
  dueAt: string;
  status: string;
  priority?: TaskPriority;
  startedAt?: string | null;
  completedAt: string | null;
  delayReason: string | null;
  closedLate: boolean;
  overdue: boolean;
  assignedBy: { id: string; name: string | null; orgRole: OrgRole; initials?: string | null };
  assignedTo: { id: string; name: string | null; orgRole: OrgRole; initials?: string | null };

  // --- handed in, and the decision on it ---
  submittedAt?: string | null;
  hasProof?: boolean;
  proofNote?: string | null;
  reviewedBy?: { id: string; name: string | null; orgRole: OrgRole } | null;
  /** Why it was sent back. Present only while it is owed again. */
  revisionNote?: string | null;
  revisionCount?: number;
}

/**
 * The four states the design draws, derived rather than stored.
 *
 * STARTED, not "doing": the state means the assignee has PICKED THE TASK UP —
 * `startedAt` is stamped the moment they accept it. "Doing" read as a claim
 * about ongoing effort that nothing here actually measures.
 */
export function taskState(
  task: TaskLike,
): 'DONE' | 'REVIEW' | 'OVERDUE' | 'STARTED' | 'OPEN' {
  if (task.status === 'DONE') return 'DONE';
  /*
    REVIEW outranks OVERDUE deliberately.
    The assignee handed the work in; whether it is accepted is now the
    reviewer's business, so the card must not keep calling them late while
    somebody else holds the decision.
  */
  if (task.status === 'SUBMITTED') return 'REVIEW';
  if (task.overdue || task.status === 'OVERDUE') return 'OVERDUE';
  if (task.startedAt) return 'STARTED';
  return 'OPEN';
}

const STATE_LABEL: Record<string, string> = {
  DONE: 'DONE',
  OVERDUE: 'OVERDUE',
  STARTED: 'STARTED',
  REVIEW: 'IN REVIEW',
  OPEN: 'OPEN',
};

/** Frame and pill per state — the whole visual language in one table. */
function skin(state: string) {
  switch (state) {
    case 'OVERDUE':
      return {
        border: '2.5px solid var(--dot-flag)',
        background: 'var(--paper)',
        pill: { background: 'var(--dot-flag)', color: '#fff', border: 'none' },
      };
    case 'REVIEW':
      // Amber: waiting on a person, not on the work.
      return {
        border: '2px solid color-mix(in oklch, var(--dot-offsite) 55%, transparent)',
        background: 'color-mix(in oklch, var(--dot-offsite) 14%, transparent)',
        pill: {
          background: 'color-mix(in oklch, var(--dot-offsite) 25%, transparent)',
          color: 'var(--dot-offsite)',
          border: '1.5px solid color-mix(in oklch, var(--dot-offsite) 45%, transparent)',
        },
      };
    case 'STARTED':
      return {
        border: '2px solid var(--ink)',
        background: 'var(--paper)',
        pill: { background: 'transparent', color: 'var(--ink)', border: '1.5px solid var(--ink)' },
      };
    case 'DONE':
      /*
        Finished work reads GREEN, not crossed out.
        A struck-through title says "discard this"; these are the tasks people
        scroll back to precisely because they were completed. The tint is mixed
        with `transparent` rather than hard-coded, so it sits on whatever paper
        the theme provides and inverts correctly in dark mode.
      */
      return {
        border: '2px solid color-mix(in oklch, var(--dot-appreciation) 45%, transparent)',
        background: 'color-mix(in oklch, var(--dot-appreciation) 22%, transparent)',
        pill: {
          background: 'color-mix(in oklch, var(--dot-appreciation) 30%, transparent)',
          color: 'var(--dot-appreciation)',
          border: '1.5px solid color-mix(in oklch, var(--dot-appreciation) 45%, transparent)',
        },
      };
    default:
      return {
        border: '2px dashed var(--rule)',
        background: 'var(--paper)',
        pill: {
          background: 'transparent',
          color: 'var(--ink-faint)',
          border: '1.5px solid var(--rule)',
        },
      };
  }
}

function when(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  if (sameDay) return `today ${time}`;

  const yesterday = new Date(today.getTime() - 86_400_000);
  if (d.toDateString() === yesterday.toDateString()) return `yesterday ${time}`;

  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${time}`;
}

/* -------------------------------------------------------------------------- */
/* Stat tiles (6a)                                                            */
/* -------------------------------------------------------------------------- */

export function TaskStats({
  open,
  started,
  overdue,
}: {
  open: number;
  started: number;
  overdue: number;
}) {
  const tiles = [
    { n: open, label: 'Open', border: '2px solid var(--ink)', tone: 'var(--ink)' },
    { n: started, label: 'Started', border: '2px dashed var(--rule)', tone: 'var(--ink)' },
    { n: overdue, label: 'Overdue', border: '2px dashed var(--dot-flag)', tone: 'var(--dot-flag)' },
  ];

  return (
    <div className="mb-4 flex gap-2">
      {tiles.map((t) => (
        <div
          key={t.label}
          className="flex flex-1 flex-col rounded-[12px] px-3.5 py-3"
          style={{ border: t.border, background: 'var(--paper)' }}
        >
          <span className="font-clock text-[20px] font-bold leading-none" style={{ color: t.tone }}>
            {t.n}
          </span>
          <span className="mt-1.5 text-[12.5px] text-ink-faint">{t.label}</span>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Progress rule (6b)                                                         */
/* -------------------------------------------------------------------------- */

export function TaskProgress({ done, total }: { done: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div className="mb-4 flex items-center gap-2.5">
      <div className="h-[9px] flex-1 overflow-hidden rounded-full border border-rule bg-paper">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${pct}%`, background: 'var(--brand-ramp)' }}
        />
      </div>
      <span className="label-mono flex-none text-ink-faint">
        {done}/{total} done
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The card                                                                   */
/* -------------------------------------------------------------------------- */

export function TaskCard({
  task,
  own,
  viewerId,
  onMarkDone,
  onStart,
  onWithdraw,
  onReview,
  onHistory,
}: {
  task: TaskLike;
  /** True on "my tasks" — the row shows who gave it, and offers Start/Done. */
  own: boolean;
  /**
   * Who is looking. Needed because this list now shows work handed out by OTHER
   * people too, so the card can name the assigner and can tell whether this
   * viewer is allowed to withdraw it.
   */
  viewerId?: string;
  onMarkDone: (task: TaskLike) => void;
  onStart: (task: TaskLike) => void;
  onWithdraw: (task: TaskLike) => void;
  /** Reviewer opens the proof and decides. Absent for one's own tasks. */
  onReview?: (task: TaskLike) => void;
  /** Open the full trail: every hand-in, every send-back, and who did it. */
  onHistory?: (task: TaskLike) => void;
}) {
  const state = taskState(task);
  const s = skin(state);
  const done = state === 'DONE';

  return (
    <div className="rounded-[16px] p-4" style={{ border: s.border, background: s.background }}>
      <div className="flex items-start gap-3">
        {/* The design's checkbox: empty, hatched once started, ticked when done. */}
        <span
          className="mt-0.5 grid size-[22px] flex-none place-items-center rounded-[6px]"
          style={{
            border: done
              ? '2px solid var(--dot-appreciation)'
              : '2px solid var(--ink)',
            background: done
              ? 'var(--dot-appreciation)'
              : state === 'STARTED'
                ? 'repeating-linear-gradient(135deg, var(--paper-sunk) 0 4px, var(--paper) 4px 8px)'
                : 'transparent',
            color: '#fff',
          }}
        >
          {done && <Check className="size-3.5" strokeWidth={3} />}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span
              className={`text-[14.5px] font-medium ${done ? 'text-ink-soft' : 'text-ink'}`}
            >
              {task.title}
            </span>

            {task.priority === 'URGENT' && !done && (
              <span
                className="label-mono rounded-md px-1.5 py-0.5 text-[9.5px]"
                style={{ border: '1.5px solid var(--dot-flag)', color: 'var(--dot-flag)' }}
              >
                Urgent
              </span>
            )}

            <span
              className="label-mono ml-auto rounded-md px-2 py-0.5 text-[10px]"
              style={s.pill}
            >
              {STATE_LABEL[state]}
            </span>
          </div>

          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[12px] text-ink-faint">
            <AlarmClock className="size-3 flex-none" />
            {done && task.completedAt ? (
              <b className="font-semibold" style={{ color: 'var(--dot-appreciation)' }}>
                finished {when(task.completedAt)}
              </b>
            ) : (
              <>due {when(task.dueAt)}</>
            )}
            <span className="text-ink-ghost">
              ·{' '}
              {own
                ? `from ${task.assignedBy.name}`
                : `${task.assignedTo.name} · ${ROLE_LABEL[task.assignedTo.orgRole]}`}
            </span>
            {/* Oversight list: WHO set this. Without it a Head reading their
                people's work cannot tell their own instructions from another
                Head's, which is the whole point of seeing the wider list. */}
            {!own && (
              <span className="text-ink-ghost">
                ·{' '}
                {task.assignedBy.id === viewerId ? (
                  <b className="font-semibold text-ink">set by you</b>
                ) : (
                  `set by ${task.assignedBy.name}`
                )}
              </span>
            )}
            {task.startedAt && !done && (
              <span className="text-ink-ghost">· started {when(task.startedAt)}</span>
            )}
          </div>

          {task.description && !done && (
            <p className="mt-1 text-[12.5px] leading-relaxed text-ink-soft">{task.description}</p>
          )}

          {/*
            WHO SIGNED IT OFF.

            Not the same person as who set it: a Head can assign work that the
            assignee's own Manager accepts, and without this line that closure
            is invisible — the Head sees "DONE · set by you" and cannot tell
            whether anyone actually checked it, or who.
          */}
          {done && task.reviewedBy && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[12px] text-ink-faint">
              <BadgeCheck
                className="size-3 flex-none"
                style={{ color: 'var(--dot-appreciation)' }}
              />
              {task.reviewedBy.id === viewerId ? (
                <b className="font-semibold text-ink">accepted by you</b>
              ) : (
                <span>
                  accepted by {task.reviewedBy.name}
                  <span className="text-ink-ghost"> · {ROLE_LABEL[task.reviewedBy.orgRole]}</span>
                </span>
              )}
              {Boolean(task.revisionCount) && onHistory && (
                <button
                  type="button"
                  onClick={() => onHistory(task)}
                  className="text-ink-faint underline underline-offset-2 hover:text-ink"
                >
                  · sent back {task.revisionCount}× — see what happened
                </button>
              )}
            </div>
          )}

          {/* Closed by a supervisor outright — nobody handed anything in. */}
          {done && !task.reviewedBy && (
            <div className="mt-0.5 text-[12px] text-ink-ghost">closed without a hand-in</div>
          )}

          {/* Sent back: what needs fixing, said where the work is. */}
          {task.revisionNote && !done && (
            <p
              className="mt-1.5 rounded-lg px-2.5 py-1.5 text-[11.5px] leading-relaxed text-ink"
              style={{ background: 'var(--state-bad-bg)' }}
            >
              <b className="font-semibold">Sent back</b>
              {task.reviewedBy?.name ? ` by ${task.reviewedBy.name}` : ''} — “{task.revisionNote}”
              <span className="block text-ink-faint">
                The deadline has not moved.
                {Boolean(task.revisionCount && task.revisionCount > 1) && onHistory && (
                  <>
                    {' '}
                    <button
                      type="button"
                      onClick={() => onHistory(task)}
                      className="underline underline-offset-2 hover:text-ink"
                    >
                      Sent back {task.revisionCount}× — see what happened
                    </button>
                  </>
                )}
              </span>
            </p>
          )}

          {state === 'REVIEW' && (
            <p
              className="mt-1.5 rounded-lg px-2.5 py-1.5 text-[11.5px] leading-relaxed text-ink"
              style={{ background: 'var(--state-warn-bg)' }}
            >
              <b className="font-semibold">Handed in</b>
              {task.proofNote ? ` — “${task.proofNote}”` : ''}
              <span className="block text-ink-faint">
                {own ? 'Waiting on your reviewer.' : 'Waiting on your decision.'}
              </span>
            </p>
          )}

          {task.closedLate && task.delayReason && (
            <p
              className="mt-1.5 rounded-lg px-2.5 py-1.5 text-[11.5px] leading-relaxed text-ink"
              style={{ background: 'var(--state-warn-bg)' }}
            >
              <b className="font-semibold">Closed late</b> — “{task.delayReason}”
            </p>
          )}

          {!done && (
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              {own && !task.startedAt && state !== 'REVIEW' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-full px-3 text-[11.5px]"
                  onClick={() => onStart(task)}
                >
                  <Play className="mr-1 size-3" />
                  Start
                </Button>
              )}

              {/*
                MY OWN task: handing in is not the same as being done. It opens
                the proof step, and the wording says so — the previous "Mark
                done" promised something the assignee no longer decides.
              */}
              {own && state !== 'REVIEW' && (
                <Button
                  size="sm"
                  className="btn-brand h-7 rounded-full px-3 text-[11.5px]"
                  onClick={() => onMarkDone(task)}
                >
                  <Check className="mr-1 size-3" />
                  Hand in
                </Button>
              )}

              {/* Handed in and waiting: nothing for the assignee to do. */}
              {own && state === 'REVIEW' && (
                <span className="text-[11.5px] text-ink-faint">Waiting on review</span>
              )}

              {/* THEIR task, handed in: this is the decision. */}
              {!own && state === 'REVIEW' && onReview && (
                <Button
                  size="sm"
                  className="btn-brand h-7 rounded-full px-3 text-[11.5px]"
                  onClick={() => onReview(task)}
                >
                  Review the proof
                </Button>
              )}

              {/* THEIR task, not handed in: a supervisor may still close it. */}
              {!own && state !== 'REVIEW' && (
                <Button
                  size="sm"
                  className="btn-brand h-7 rounded-full px-3 text-[11.5px]"
                  onClick={() => onMarkDone(task)}
                >
                  <Check className="mr-1 size-3" />
                  Mark done
                </Button>
              )}

              {/* Only the person who gave the instruction may take it back —
                  the server enforces this, so showing it to anyone else would
                  be offering a button that always fails. */}
              {!own && task.assignedBy.id === viewerId && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-full px-3 text-[11.5px] text-flag"
                  onClick={() => onWithdraw(task)}
                >
                  Withdraw
                </Button>
              )}
            </div>
          )}
        </div>

        {!own && <PersonAvatar person={task.assignedTo} size={28} />}
      </div>
    </div>
  );
}
